"use client";

import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from "recharts";
import { Loader2 } from "lucide-react";

interface ProductionFlowData {
  date: string;
  ordered: number;
  produced: number;
}

interface ProductionFlowChartProps {
  accountId?: string | null;
}

export function ProductionFlowChart({ accountId }: ProductionFlowChartProps) {
  const [period, setPeriod] = useState<7 | 30>(7);
  const [data, setData] = useState<ProductionFlowData[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, [period, accountId]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        period: period.toString(),
      });

      if (accountId) {
        params.append("accountId", accountId);
      }

      const response = await fetch(`/api/production-flow?${params}`);
      const result = await response.json();

      if (result.success) {
        // Combine itemsIn and itemsOut data
        const combinedData: ProductionFlowData[] = result.itemsIn.map((item: any, index: number) => ({
          date: item.formattedDate,
          ordered: item.count,
          produced: result.itemsOut[index]?.count || 0,
        }));

        setData(combinedData);
      } else {
        console.error("Failed to fetch production flow data:", result.error);
        setData([]);
      }
    } catch (error) {
      console.error("Error fetching production flow data:", error);
      setData([]);
    } finally {
      setLoading(false);
    }
  };

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-3 border border-gray-200 rounded-lg shadow-lg">
          <p className="font-medium text-gray-900 mb-2">{label}</p>
          <p className="text-sm text-blue-600">
            Ordered: <span className="font-semibold">{payload[0]?.value || 0}</span>
          </p>
          <p className="text-sm text-green-600">
            Produced: <span className="font-semibold">{payload[1]?.value || 0}</span>
          </p>
        </div>
      );
    }
    return null;
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <CardTitle className="text-base sm:text-lg">Production Flow</CardTitle>
            <CardDescription className="text-xs sm:text-sm">Items ordered vs items produced</CardDescription>
          </div>
          <div className="flex gap-2">
            <Button
              variant={period === 7 ? "default" : "outline"}
              size="sm"
              onClick={() => setPeriod(7)}
              disabled={loading}
              className="text-xs sm:text-sm"
            >
              Last 7 Days
            </Button>
            <Button
              variant={period === 30 ? "default" : "outline"}
              size="sm"
              onClick={() => setPeriod(30)}
              disabled={loading}
              className="text-xs sm:text-sm"
            >
              Last 30 Days
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex flex-col items-center justify-center h-[250px] sm:h-[300px]">
            <Loader2 className="h-8 w-8 animate-spin text-slate-400 mb-2" />
            <span className="text-xs sm:text-sm text-slate-500">Loading production data...</span>
          </div>
        ) : data.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-[250px] sm:h-[300px]">
            <p className="text-xs sm:text-sm text-slate-500">No production data available</p>
          </div>
        ) : (
          <ResponsiveContainer width="100%" height={250}>
            <LineChart data={data} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="date" stroke="#888888" fontSize={12} tickLine={false} axisLine={false} />
              <YAxis
                stroke="#888888"
                fontSize={12}
                tickLine={false}
                axisLine={false}
                tickFormatter={(value) => `${value}`}
              />
              <Tooltip content={<CustomTooltip />} />
              <Legend wrapperStyle={{ paddingTop: "20px" }} iconType="line" />
              <Line
                type="monotone"
                dataKey="ordered"
                stroke="#3b82f6"
                strokeWidth={2}
                name="Ordered"
                dot={false}
                activeDot={{ r: 6, fill: "#3b82f6" }}
              />
              <Line
                type="monotone"
                dataKey="produced"
                stroke="#10b981"
                strokeWidth={2}
                name="Produced"
                dot={false}
                activeDot={{ r: 6, fill: "#10b981" }}
              />
            </LineChart>
          </ResponsiveContainer>
        )}
      </CardContent>
    </Card>
  );
}
